/**
 * Sticker Shop / collection screen (NAMED-ONLY — docs/BARRAGE_IMPLEMENTATION_PLAN.md
 * §2 "Tiered auth", P3/P4). Browse the public sticker catalog the way the legacy
 * POPSTAR app did — the **Type → Category → Series → Sticker** taxonomy
 * (docs/legacy/AVOID_LEGACY_ANALYSIS.md §2.5) — see which stickers you own vs.
 * which are locked, buy locked ones with tokens (§2.6 economy), and equip owned
 * ones into one of the two slots (§2.6 "Equip & trade").
 *
 * UX mirrors the legacy drill-down (pick a TYPE tab → pick a CATEGORY → the grid
 * shows each SERIES as a section of up to 8 cards) rather than collapsing the
 * hierarchy into anonymous dropdowns, so the showbiz "collection" feel survives.
 *
 * Presentational + thin orchestration: it holds only screen-local UI state and
 * talks to the backend ONLY through the {@link GameServices} facade. The SERVER is
 * the source of truth for the wallet, ownership and prices — this screen never
 * debits tokens itself; it calls {@link GameServices.buySticker} / {@link
 * GameServices.equipSticker} and re-reads the authoritative profile/ownership
 * afterwards (docs/BARRAGE_IMPLEMENTATION_PLAN.md §4 "Anti-cheat").
 *
 * Because the whole collection economy is named-only, anonymous callers will see
 * {@link RequiresAccountError} surfaced as the "log in to collect" tease. This
 * component does NOT decide the tier — the parent should only mount it for named
 * users — but it degrades gracefully if a network call throws that error.
 */
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useT } from './i18n';
import { canAfford, stickerPrice } from '../core/economy';
import type { GameServices } from '../services/types';
import { RequiresAccountError } from '../services/types';
import type { StickerCatalogItem, UserProfile } from '../services/dataModel';

interface StickerShopProps {
  /** The tier-aware data facade. Buy/equip/profile calls are NAMED-ONLY. */
  services: GameServices;
  /** Return to the previous screen (parent owns navigation). */
  onBack: () => void;
  /** When true (inside the POPSTAR tab shell) suppress this screen's own header. */
  embedded?: boolean;
  /** Notify the parent that the token balance changed (a buy), to refresh chrome. */
  onEconomyChange?: () => void;
}

/** A catalog item paired with the live ownership / equip state the UI draws from. */
interface ShopEntry {
  item: StickerCatalogItem;
  owned: boolean;
  equippedSlot: 1 | 2 | null;
  /** True when the user could buy it right now (locked + affordable). */
  affordable: boolean;
}

/** One series' worth of cards, in the grid order the legacy app rendered them. */
interface SeriesGroup {
  series: string;
  entries: ShopEntry[];
}

/** Per-`type` filter labels (legacy: 001=regular, 002=seasonal, 003=secret). */
const TYPE_TABS: { id: StickerCatalogItem['type']; label: string; hint: string }[] = [
  { id: 'regular', label: 'Regular', hint: 'The core collection.' },
  { id: 'seasonal', label: 'Seasonal', hint: 'Limited monthly drops.' },
  { id: 'secret', label: 'Secret', hint: 'Rare hidden finds.' },
];

/** Sentinel used by the category selector to mean "every category". */
const ALL = '__all__';

/** A friendlier label for a raw category/series code (e.g. "001" → "Series 1"). */
function prettyCode(prefix: string, code: string): string {
  const n = Number(code);
  return Number.isFinite(n) && String(n) !== code ? `${prefix} ${n}` : code;
}

export function StickerShop({ services, onBack, embedded = false, onEconomyChange }: StickerShopProps) {
  const t = useT();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [needsAccount, setNeedsAccount] = useState(false);

  const [catalog, setCatalog] = useState<StickerCatalogItem[]>([]);
  const [owned, setOwned] = useState<Set<string>>(new Set());
  const [profile, setProfile] = useState<UserProfile | null>(null);

  // Filter selections: type tab + category narrowing (series shows as sections).
  const [typeTab, setTypeTab] = useState<StickerCatalogItem['type']>('regular');
  const [category, setCategory] = useState<string>(ALL);

  // The sticker open in the detail panel (by id), and a transient action state.
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [busy, setBusy] = useState<null | 'buy' | 'equip1' | 'equip2'>(null);
  const [flash, setFlash] = useState<string | null>(null);

  const tokens = profile?.tokens ?? 0;

  /** Pull the catalog + ownership + profile together (named-only network reads). */
  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    setNeedsAccount(false);
    try {
      const [cat, own, prof] = await Promise.all([
        services.getStickerCatalog(),
        services.getOwnedStickers(),
        services.getProfile(),
      ]);
      setCatalog(cat);
      setOwned(new Set(own));
      setProfile(prof);
    } catch (err) {
      if (err instanceof RequiresAccountError) {
        setNeedsAccount(true);
      } else {
        setError(err instanceof Error ? err.message : 'Could not load the shop.');
      }
    } finally {
      setLoading(false);
    }
  }, [services]);

  useEffect(() => {
    void load();
  }, [load]);

  /** Which slot (if any) a sticker id is currently equipped into. */
  const slotOf = useCallback(
    (id: string): 1 | 2 | null => {
      if (!profile) return null;
      if (profile.equipped.slot1 === id) return 1;
      if (profile.equipped.slot2 === id) return 2;
      return null;
    },
    [profile],
  );

  /** Catalog rows for the active type tab, decorated with live ownership state. */
  const typeEntries = useMemo<ShopEntry[]>(
    () =>
      catalog
        .filter((item) => item.type === typeTab)
        .map((item) => {
          const isOwned = owned.has(item.id);
          return {
            item,
            owned: isOwned,
            equippedSlot: slotOf(item.id),
            affordable: !isOwned && canAfford(tokens, stickerPrice(item.rarity)),
          };
        }),
    [catalog, typeTab, owned, slotOf, tokens],
  );

  /** Distinct categories available within the active type tab (legacy §2.5). */
  const categories = useMemo(
    () => Array.from(new Set(typeEntries.map((e) => e.item.category))).sort(),
    [typeEntries],
  );

  /**
   * The visible cards: the type-tab rows narrowed by the chosen category and then
   * GROUPED BY SERIES (the legacy browse drilled Type → Category → Series → s1–s8),
   * each group sorted by sticker number for the stable 8-up grid.
   */
  const groups = useMemo<SeriesGroup[]>(() => {
    const inCategory = typeEntries.filter(
      (e) => category === ALL || e.item.category === category,
    );
    const bySeries = new Map<string, ShopEntry[]>();
    for (const e of inCategory) {
      const list = bySeries.get(e.item.series) ?? [];
      list.push(e);
      bySeries.set(e.item.series, list);
    }
    return Array.from(bySeries.entries())
      .sort((a, b) => a[0].localeCompare(b[0]))
      .map(([series, entries]) => ({
        series,
        entries: entries.sort((a, b) => a.item.num - b.item.num),
      }));
  }, [typeEntries, category]);

  /** Collection progress for the active (type + category) view. */
  const progress = useMemo(() => {
    const visible = typeEntries.filter(
      (e) => category === ALL || e.item.category === category,
    );
    return { owned: visible.filter((e) => e.owned).length, total: visible.length };
  }, [typeEntries, category]);

  const selected = useMemo<ShopEntry | null>(
    () => typeEntries.find((e) => e.item.id === selectedId) ?? null,
    [typeEntries, selectedId],
  );

  /** Reset the dependent filters / selection when the type tab changes. */
  const onSelectType = useCallback((id: StickerCatalogItem['type']) => {
    setTypeTab(id);
    setCategory(ALL);
    setSelectedId(null);
  }, []);

  /** Show a transient confirmation toast. */
  const toast = useCallback((msg: string) => {
    setFlash(msg);
    window.setTimeout(() => setFlash((cur) => (cur === msg ? null : cur)), 2400);
  }, []);

  /** Buy the selected sticker with tokens; server enforces price + balance. */
  const onBuy = useCallback(async () => {
    if (!selected || selected.owned) return;
    setBusy('buy');
    setError(null);
    try {
      await services.buySticker(selected.item.id);
      toast(`Bought ${selected.item.title}!`);
      await load(); // re-read authoritative ownership + token balance.
      onEconomyChange?.(); // tokens changed — refresh the shell's chrome card.
    } catch (err) {
      if (err instanceof RequiresAccountError) setNeedsAccount(true);
      else setError(err instanceof Error ? err.message : 'Purchase failed.');
    } finally {
      setBusy(null);
    }
  }, [selected, services, toast, load, onEconomyChange]);

  /** Equip the selected (owned) sticker into a slot; server checks ownership. */
  const onEquip = useCallback(
    async (slot: 1 | 2) => {
      if (!selected || !selected.owned) return;
      setBusy(slot === 1 ? 'equip1' : 'equip2');
      setError(null);
      try {
        await services.equipSticker(slot, selected.item.id);
        toast(`Equipped to slot ${slot}.`);
        await load();
      } catch (err) {
        if (err instanceof RequiresAccountError) setNeedsAccount(true);
        else setError(err instanceof Error ? err.message : 'Could not equip.');
      } finally {
        setBusy(null);
      }
    },
    [selected, services, toast, load],
  );

  /** A single sticker card (owned / locked / equipped states). */
  const renderCard = (e: ShopEntry) => (
    <button
      key={e.item.id}
      type="button"
      className={
        'sticker-cell' +
        (e.owned ? ' owned' : ' locked') +
        (selectedId === e.item.id ? ' selected' : '') +
        (e.equippedSlot ? ' equipped' : '')
      }
      aria-pressed={selectedId === e.item.id}
      aria-label={`${e.item.title}${e.owned ? '' : ' (locked)'}`}
      onClick={() => setSelectedId(e.item.id)}
    >
      <span className={'sticker-thumb rarity-' + e.item.rarity}>
        {e.item.imageUrl ? (
          <img src={e.item.imageUrl} alt="" draggable={false} />
        ) : (
          <span className="sticker-thumb-ph" aria-hidden="true">
            {e.item.num}
          </span>
        )}
        {!e.owned && (
          <span className="sticker-lock" aria-hidden="true">
            🔒
          </span>
        )}
        {e.equippedSlot && (
          <span className="sticker-badge" aria-hidden="true">
            {e.equippedSlot}
          </span>
        )}
      </span>
      <span className="sticker-cell-name">{e.item.title}</span>
      <span className="sticker-cell-tag">
        {e.owned ? (
          t('shop.owned')
        ) : (
          <span className={e.affordable ? 'sticker-cell-price' : 'sticker-cell-price short'}>
            <span className="token-coin" aria-hidden="true">
              ◉
            </span>
            {stickerPrice(e.item.rarity).toLocaleString()}
          </span>
        )}
      </span>
    </button>
  );

  return (
    <div className={'screen shop' + (embedded ? ' is-embedded' : '')}>
      <style>{SHOP_CSS}</style>

      {!embedded && (
        <header className="shop-head">
          <button className="btn btn-ghost shop-back" onClick={onBack}>
            ‹ Back
          </button>
          <h1 className="shop-title">{t('shop.title')}</h1>
          <div className="shop-wallet" title="Token balance">
            <span className="token-coin" aria-hidden="true">
              ◉
            </span>
            <span className="token-amount">{tokens.toLocaleString()}</span>
          </div>
        </header>
      )}

      {needsAccount ? (
        <section className="panel tease">
          <h2>Log in to collect</h2>
          <p className="tease-copy">
            Stickers, tokens and the shop unlock with a free account. Anonymous play
            keeps the game and the leaderboard — sign up to start collecting.
          </p>
        </section>
      ) : loading ? (
        <p className="shop-status">Loading the shop…</p>
      ) : error && catalog.length === 0 ? (
        <section className="panel">
          <p className="shop-error">{error}</p>
          <button className="btn btn-ghost" onClick={() => void load()}>
            Retry
          </button>
        </section>
      ) : (
        <>
          {/* Type → Category drill-down (legacy taxonomy §2.5). */}
          <div className="shop-tabs" role="tablist" aria-label="Sticker type">
            {TYPE_TABS.map((tab) => (
              <button
                key={tab.id}
                role="tab"
                aria-selected={typeTab === tab.id}
                className={typeTab === tab.id ? 'shop-tab active' : 'shop-tab'}
                onClick={() => onSelectType(tab.id)}
              >
                {t('shop.' + (tab.id === 'seasonal' ? 'seasonalType' : tab.id), tab.label)}
              </button>
            ))}
          </div>

          {/* Category chip row — the second level of the legacy drill-down. */}
          {categories.length > 0 && (
            <div
              className="shop-cats"
              role="tablist"
              aria-label="Sticker category"
            >
              <button
                role="tab"
                aria-selected={category === ALL}
                className={category === ALL ? 'shop-chip active' : 'shop-chip'}
                onClick={() => {
                  setCategory(ALL);
                  setSelectedId(null);
                }}
              >
                {t('shop.all')}
              </button>
              {categories.map((c) => (
                <button
                  key={c}
                  role="tab"
                  aria-selected={category === c}
                  className={category === c ? 'shop-chip active' : 'shop-chip'}
                  onClick={() => {
                    setCategory(c);
                    setSelectedId(null);
                  }}
                >
                  {prettyCode('Cat', c)}
                </button>
              ))}
            </div>
          )}

          {/* Collection progress for the current view. */}
          <p className="shop-progress">
            <span className="shop-progress-hint">
              {TYPE_TABS.find((t) => t.id === typeTab)?.hint}
            </span>
            <span className="shop-progress-count">
              {progress.owned}/{progress.total} {t('shop.collected')}
            </span>
          </p>

          {/* The collection grid, grouped by series (owned vs locked). */}
          {groups.length === 0 ? (
            <p className="shop-status">No stickers match these filters yet.</p>
          ) : (
            <div className="shop-series-list">
              {groups.map((g) => (
                <section key={g.series} className="shop-series">
                  {/* Only badge the series header when it disambiguates (>1 series). */}
                  {groups.length > 1 && (
                    <h2 className="shop-series-title">{prettyCode('Series', g.series)}</h2>
                  )}
                  <div className="sticker-grid">{g.entries.map(renderCard)}</div>
                </section>
              ))}
            </div>
          )}

          {/* A non-fatal reload error (we kept the last catalog on screen). */}
          {error && catalog.length > 0 && <p className="shop-error">{error}</p>}

          {/* Detail / action panel for the selected sticker. */}
          {selected && (
            <section className="panel sticker-detail">
              <div className="sticker-detail-head">
                <span className={'sticker-thumb large rarity-' + selected.item.rarity}>
                  {selected.item.imageUrl ? (
                    <img src={selected.item.imageUrl} alt="" draggable={false} />
                  ) : (
                    <span className="sticker-thumb-ph" aria-hidden="true">
                      {selected.item.num}
                    </span>
                  )}
                  {selected.equippedSlot && (
                    <span className="sticker-badge" aria-hidden="true">
                      {selected.equippedSlot}
                    </span>
                  )}
                </span>
                <div className="sticker-detail-meta">
                  <h2>{selected.item.title}</h2>
                  <p className="sticker-rarity">{selected.item.rarity}</p>
                  <p className="sticker-detail-path">
                    {TYPE_TABS.find((t) => t.id === selected.item.type)?.label} ·{' '}
                    {prettyCode('Cat', selected.item.category)} ·{' '}
                    {prettyCode('Series', selected.item.series)} · #{selected.item.num}
                  </p>
                  <p className="sticker-desc">{selected.item.desc}</p>
                </div>
              </div>

              <div className="sticker-detail-actions">
                {selected.owned ? (
                  <>
                    <button
                      className={
                        'btn ' +
                        (selected.equippedSlot === 1 ? 'btn-primary' : 'btn-ghost')
                      }
                      disabled={busy !== null || selected.equippedSlot === 1}
                      onClick={() => void onEquip(1)}
                    >
                      {busy === 'equip1'
                        ? 'Equipping…'
                        : selected.equippedSlot === 1
                          ? 'In slot 1'
                          : 'Equip slot 1'}
                    </button>
                    <button
                      className={
                        'btn ' +
                        (selected.equippedSlot === 2 ? 'btn-primary' : 'btn-ghost')
                      }
                      disabled={busy !== null || selected.equippedSlot === 2}
                      onClick={() => void onEquip(2)}
                    >
                      {busy === 'equip2'
                        ? 'Equipping…'
                        : selected.equippedSlot === 2
                          ? 'In slot 2'
                          : 'Equip slot 2'}
                    </button>
                  </>
                ) : (
                  <button
                    className="btn btn-primary buy-btn"
                    disabled={busy !== null || !selected.affordable}
                    onClick={() => void onBuy()}
                  >
                    {busy === 'buy' ? (
                      'Buying…'
                    ) : (
                      <>
                        {t('shop.buy')}
                        <span className="buy-price">
                          <span className="token-coin" aria-hidden="true">
                            ◉
                          </span>
                          {stickerPrice(selected.item.rarity).toLocaleString()}
                        </span>
                      </>
                    )}
                  </button>
                )}
              </div>

              {!selected.owned && !selected.affordable && (
                <p className="shop-note">
                  Not enough tokens — spin the wheel to earn more.
                </p>
              )}
            </section>
          )}
        </>
      )}

      {flash && (
        <div className="shop-toast" role="status">
          {flash}
        </div>
      )}
    </div>
  );
}

/**
 * Screen-scoped styles for the rebuilt collection drill-down (nested under `.shop`
 * so nothing leaks). The base shop primitives (`.shop-head`, `.shop-tabs`,
 * `.sticker-grid`, `.sticker-cell`, `.sticker-thumb`, `.sticker-detail`, the
 * rarity rings, `.shop-toast`, the POPSTAR palette re-map, every CSS var) come
 * from src/index.css; only the NEW pieces this rebuild introduced — the category
 * chip row, the per-view progress line, the series sections, and the per-card
 * price tag — live here. All colours read from the design tokens (no ad-hoc
 * values), consistent with the sibling network screens.
 */
const SHOP_CSS = `
/* Category chip row — the second level of the Type → Category → Series drill-down. */
.shop .shop-cats {
  display: flex;
  gap: var(--space-2);
  flex-wrap: wrap;
  justify-content: center;
  width: min(560px, 94vw);
}
.shop .shop-chip {
  font: inherit;
  font-weight: 600;
  font-size: 13px;
  color: var(--text-dim);
  background: var(--panel-2);
  border: 1px solid var(--glass-border);
  border-radius: var(--radius-pill);
  padding: 6px 14px;
  cursor: pointer;
  transition: background var(--t-fast) var(--ease),
    border-color var(--t-fast) var(--ease), color var(--t-fast) var(--ease);
}
.shop .shop-chip:hover {
  border-color: var(--accent);
  color: var(--text);
}
.shop .shop-chip.active {
  color: var(--accent-ink);
  background: linear-gradient(135deg, var(--accent-soft), var(--accent));
  border-color: transparent;
}

/* Collection progress line for the current (type + category) view. */
.shop .shop-progress {
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: var(--space-4);
  width: min(560px, 94vw);
  margin: 0;
}
.shop .shop-progress-hint {
  color: var(--muted);
  font-size: 13px;
}
.shop .shop-progress-count {
  color: var(--gold);
  font-weight: 700;
  font-size: 13px;
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

/* Series sections — each is one row of the legacy s1–s8 grid. */
.shop .shop-series-list {
  display: flex;
  flex-direction: column;
  gap: var(--space-5);
  width: min(560px, 94vw);
}
.shop .shop-series {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}
.shop .shop-series-title {
  margin: 0;
  font-size: 13px;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.08em;
  font-weight: 700;
}

/* Per-card status tag: "Owned" or the token price under each thumbnail. */
.shop .sticker-cell-tag {
  font-size: 11px;
  line-height: 1.2;
  color: var(--muted);
  text-transform: uppercase;
  letter-spacing: 0.04em;
}
.shop .sticker-cell.owned .sticker-cell-tag {
  color: var(--success);
  font-weight: 700;
}
.shop .sticker-cell-price {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  font-variant-numeric: tabular-nums;
  font-weight: 700;
  color: var(--gold);
}
.shop .sticker-cell-price.short {
  color: var(--muted);
}
.shop .sticker-cell-price .token-coin {
  font-size: 12px;
}

/* Detail panel: the Type · Cat · Series · # breadcrumb under the rarity. */
.shop .sticker-detail-path {
  margin: 0 0 6px;
  font-size: 12px;
  color: var(--text-dim);
  font-variant-numeric: tabular-nums;
}
`;
